// Admin dashboard: API calls and wording. Types come from the Worker so they can't drift.
import type { CreditGrant, GrantDuration } from "../../shared/ai-credits";
import { formatDate } from "../../shared/dates";
import type { AdminOverview, AdminUser, AdminWorkspace } from "../../worker/ops/admin";
import type { GateMetrics } from "../../worker/ops/metrics";
import { api } from "./api";

export type { AdminOverview, AdminUser, AdminWorkspace, GateMetrics };

export const getOverview = () => api<AdminOverview>("/api/ops/overview");
export const getGate = () => api<GateMetrics>("/api/ops/metrics");
export const searchUsers = (q: string) => api<{ users: AdminUser[] }>(`/api/ops/users?q=${encodeURIComponent(q)}`).then((r) => r.users);

export type GrantRequest =
  | { kind: "bonus"; credits: number; note?: string }
  | {
      kind: "limits";
      dailyLimit: number | null;
      monthlyLimit: number | null;
      duration: GrantDuration;
      note?: string;
    };

const userOf = (r: Promise<{ user: AdminUser }>) => r.then((b) => b.user);
export const addGrant = (userId: string, body: GrantRequest) => userOf(api(`/api/ops/users/${userId}/grants`, { method: "POST", json: body }));
export const makePermanent = (userId: string, grantId: string) =>
  userOf(
    api(`/api/ops/users/${userId}/grants/${grantId}/permanent`, {
      method: "POST",
    }),
  );
export const revokeGrant = (userId: string, grantId: string) => userOf(api(`/api/ops/users/${userId}/grants/${grantId}`, { method: "DELETE" }));

export const DURATION_LABELS: Record<GrantDuration, string> = {
  today: "Today only",
  week: "7 days",
  month: "30 days",
  permanent: "Permanently",
};

const n = (v: number) => v.toLocaleString("en-IN");

/** "+200 credits today" or "Daily 500, monthly 5,000 · until 8 Nov 2026". */
export function grantLine(g: CreditGrant, today: string): string {
  if (g.kind === "bonus") return `+${n(g.credits ?? 0)} extra credits ${g.startsOn === today ? "today" : `on ${formatDate(g.startsOn)}`}`;
  const parts = [g.dailyLimit !== null && `daily ${n(g.dailyLimit)}`, g.monthlyLimit !== null && `monthly ${n(g.monthlyLimit)}`].filter(Boolean).join(", ");
  const until = g.endsOn === null ? "permanent" : g.endsOn === today ? "today only" : `until ${formatDate(g.endsOn)}`;
  return `Limits: ${parts} · ${until}`;
}

export type WorkspaceChange = { storageQuotaBytes?: number | null; suspended?: boolean; reason?: string };
/** Changes a workspace; returns its members, updated. */
export const updateWorkspace = (orgId: string, body: WorkspaceChange) =>
  api<{ users: AdminUser[] }>(`/api/ops/workspaces/${orgId}`, { method: "PATCH", json: body }).then((r) => r.users);

export const GB = 1024 ** 3;
/** "1.5" GB as typed in the form, to bytes; null for an empty box. */
export const gbToBytes = (v: string) => (v.trim() === "" ? null : Math.round(Number(v) * GB));
